import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "./fordon";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { besiktningFaq, besiktningSections } from "@/data/nationella-guider-2";

const URL = "https://www.akhusbilfritid.se/besiktning-husbil";

export const Route = createFileRoute("/besiktning-husbil")({
  // FAQ-schemat byggs i loadern så guidens textdata hamnar i sidans egen
  // chunk i stället för i initial-bundlen.
  codeSplitGroupings: [["loader", "component"]],
  loader: () => ({ faqLd: faqSchema(besiktningFaq) }),
  head: ({ loaderData }) => ({
    meta: [
      { title: "Besiktning husbil – intervall, pris & vanligaste felen" },
      {
        name: "description",
        content:
          "När husbilen ska besiktigas, vad det kostar 2026, de sex vanligaste underkännandena från vår verkstad och en egenkontroll på 20 minuter.",
      },
      {
        name: "keywords",
        content:
          "besiktning husbil, husbil besiktning pris, kontrollbesiktning husbil, husbil underkänd besiktning, gasolkontroll husbil",
      },
      { property: "og:type", content: "article" },
      { property: "og:title", content: "Besiktning av husbil – intervall, pris & de vanligaste felen" },
      {
        property: "og:description",
        content:
          "Intervall och priser 2026, de sex vanligaste underkännandena och en egenkontroll på 20 minuter före besiktningen.",
      },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "@id": `${URL}#article`,
          headline: "Besiktning av husbil – intervall, pris och de vanligaste felen",
          url: URL,
          inLanguage: "sv-SE",
          about: "Kontrollbesiktning av husbil i Sverige",
          author: { "@type": "Organization", name: "AK Husbil & Fritid" },
          publisher: { "@type": "Organization", name: "AK Husbil & Fritid" },
        }),
      },
      ...(loaderData ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.faqLd) }] : []),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
            { "@type": "ListItem", position: 2, name: "Guider", item: "https://www.akhusbilfritid.se/guider" },
            { "@type": "ListItem", position: 3, name: "Besiktning av husbil", item: URL },
          ],
        }),
      },
    ],
  }),
  component: BesiktningPage,
});

const facts = [
  { k: "36 / 24 / 12 mån", v: "besiktningsintervall från första registrering" },
  { k: "500–800 kr", v: "pris under 3 500 kg totalvikt" },
  { k: "6 år", v: "rekommenderat däckbyte oavsett mönsterdjup" },
  { k: "0", v: "punkter i boendedelen som besiktningen kontrollerar" },
];

function BesiktningPage() {
  return (
    <>
      <PageHeader
        label="Nationell guide · Besiktning"
        title={
          <>
            Besiktning av husbil — <span className="font-italic-display text-accent">godkänd</span> första gången.
          </>
        }
        sub="Intervall och priser, de sex fel vi oftast åtgärdar i vår verkstad före besiktning, och en egenkontroll på 20 minuter som tar bort de flesta anmärkningarna."
      />

      <section className="container-edge pt-14 md:pt-20">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {facts.map((f) => (
            <div key={f.k} className="rounded-3xl border border-border bg-card p-6">
              <p className="font-display text-3xl leading-none text-accent">{f.k}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.v}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="pt-14 md:pt-20">
        <HubEditorial eyebrow="Guiden" sections={besiktningSections} faq={besiktningFaq} />
      </div>

      <section className="container-edge pb-20 md:pb-24">
        <div className="rounded-3xl border border-border bg-warm p-7 md:p-10">
          <p className="eyebrow">Läs vidare</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <Link
              to="/fukttest-husbil"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Fukttest — det besiktningen aldrig tittar på</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/husbilsforsakring"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Husbilsförsäkring — pris och villkor</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/kontakt"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Boka verkstad i Kalix före besiktningen</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
